import { useState, useRef, useEffect } from "react";
import { Play, Pause } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Animated waveform bars shown while audio is playing
const Waveform = ({ playing }) => (
  <div className="flex items-center gap-[3px] h-4">
    {[1, 1.6, 0.8, 1.3, 0.6, 1.1, 1.4, 0.9].map((scale, i) => (
      <span
        key={i}
        className="w-[3px] rounded-full bg-scoop-blue transition-all"
        style={{
          height: playing ? `${scale * 10}px` : "4px",
          animation: playing
            ? `wave ${0.55 + i * 0.07}s ease-in-out infinite alternate`
            : "none",
          animationDelay: `${i * 0.06}s`,
          opacity: playing ? 1 : 0.4,
        }}
      />
    ))}
  </div>
);

// Two-mode read-aloud player.
//   mode="preview": ~60s teaser on the homepage hero. When it ends it invites
//                   the listener into the full story via onReadStory (the same
//                   modal every other homepage tap uses — no paradigm switch).
//   mode="full":    complete narration on the /story page.
// Audio is PREFETCHED on mount (backend pre-generates at ingestion), so play
// is normally instant. Unmount hard-stops playback — navigating away must
// never leave a voice running with no controls on screen.
export const AudioOverview = ({ article, mode = "preview", onReadStory = null }) => {
  const [state, setState] = useState("idle"); // idle | loading | playing | paused | error
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [finished, setFinished] = useState(false);
  const audioRef = useRef(null);
  const blobUrlRef = useRef(null);
  const ensurePromiseRef = useRef(null);

  const ensureAudio = () => {
    if (audioRef.current) return Promise.resolve(audioRef.current);
    if (!ensurePromiseRef.current) {
      ensurePromiseRef.current = (async () => {
        const res = await fetch(`${API}/articles/${article.id}/audio?mode=${mode}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = URL.createObjectURL(blob);

        const audio = new Audio(blobUrlRef.current);
        audio.addEventListener("loadedmetadata", () => setDuration(audio.duration));
        audio.addEventListener("timeupdate", () => {
          setCurrentTime(audio.currentTime);
          setProgress(audio.duration ? audio.currentTime / audio.duration : 0);
        });
        audio.addEventListener("ended", () => {
          setState("paused");
          setProgress(1);
          setFinished(true);
        });
        audioRef.current = audio;
        return audio;
      })().catch((err) => {
        ensurePromiseRef.current = null; // allow retry on next click
        throw err;
      });
    }
    return ensurePromiseRef.current;
  };

  // Prefetch on mount so the first tap plays instantly; hard-stop on unmount
  // or article rotation so navigation never leaves orphaned narration playing.
  useEffect(() => {
    if (article?.id) ensureAudio().catch(() => {});
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
      ensurePromiseRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [article?.id, mode]);

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  const togglePlay = async () => {
    const current = audioRef.current;
    if (current && state === "playing") {
      current.pause();
      setState("paused");
      return;
    }
    try {
      if (!audioRef.current) setState("loading");
      const audio = await ensureAudio();
      setFinished(false);
      await audio.play();
      setState("playing");
    } catch (err) {
      console.error("Audio overview error:", err);
      setState("error");
    }
  };

  const seek = (e) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    audio.currentTime = ratio * duration;
    setProgress(ratio);
  };

  const isActive = state === "playing" || state === "paused";
  const label =
    state === "loading"
      ? mode === "preview" ? "Making your sneak peek…" : "Getting the story ready…"
      : state === "error"
      ? "Couldn't load audio"
      : mode === "preview" ? "Audio Sneak Peek" : "Read Aloud";

  return (
    <>
      <style>{`
        @keyframes wave {
          from { transform: scaleY(0.4); }
          to   { transform: scaleY(1.4); }
        }
      `}</style>

      {/* Compact single-row card; extra rows appear only while listening */}
      <div
        className="rounded-lg border border-scoop-border bg-white/70 backdrop-blur-sm px-3.5 py-2.5 flex flex-col gap-2 w-full select-none"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2.5">
          <button
            onClick={togglePlay}
            disabled={state === "loading" || state === "error"}
            className="flex-shrink-0 w-10 h-10 rounded-full bg-scoop-blue text-white flex items-center justify-center shadow-sm hover:bg-scoop-blue/90 active:scale-95 transition-all disabled:opacity-50"
            aria-label={state === "playing" ? "Pause" : "Listen"}
          >
            {state === "loading" ? (
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
            ) : state === "playing" ? (
              <Pause className="w-4 h-4" />
            ) : (
              <Play className="w-4 h-4 ml-0.5" />
            )}
          </button>

          <span className="text-[0.72rem] font-bold tracking-wide text-scoop-ink whitespace-nowrap">
            {label}
          </span>
          <span className="flex-shrink-0 text-[0.6rem] font-bold tracking-widest uppercase px-1.5 py-0.5 rounded bg-violet-100 text-violet-600 border border-violet-200">
            Labs
          </span>

          {isActive && !finished && <Waveform playing={state === "playing"} />}

          {isActive && !finished && duration > 0 && (
            <span className="flex-shrink-0 text-[0.65rem] tabular-nums text-scoop-muted">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          )}
        </div>

        {/* The funnel moment: preview finished → invite into the full story,
            through the SAME modal as every other homepage tap. */}
        {mode === "preview" && finished && (
          onReadStory ? (
            <button
              onClick={onReadStory}
              className="text-left text-[0.75rem] font-bold text-scoop-blue hover:underline"
              data-testid="preview-funnel-link"
            >
              Liked the peek? Read (or hear) the whole story →
            </button>
          ) : (
            <a
              href={`/story/${article.id}`}
              className="text-[0.75rem] font-bold text-scoop-blue hover:underline"
              data-testid="preview-funnel-link"
            >
              Liked the peek? Read (or hear) the whole story →
            </a>
          )
        )}

        {/* Progress bar only while actively listening */}
        {isActive && !finished && (
          <div
            className="h-1.5 rounded-full bg-scoop-border cursor-pointer overflow-hidden focus:outline-none focus:ring-2 focus:ring-scoop-blue/50"
            onClick={seek}
            onKeyDown={(e) => {
              const audio = audioRef.current;
              if (!audio || !duration) return;
              if (e.key === "ArrowRight") audio.currentTime = Math.min(duration, audio.currentTime + 5);
              if (e.key === "ArrowLeft") audio.currentTime = Math.max(0, audio.currentTime - 5);
            }}
            role="slider"
            tabIndex={0}
            aria-label="Story position"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progress * 100)}
          >
            <div
              className="h-full rounded-full bg-scoop-blue transition-all"
              style={{ width: `${progress * 100}%` }}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default AudioOverview;
